/**
 * Herbruikbare interface-onderdelen, opgebouwd uit de design tokens.
 * Schermen gebruiken deze in plaats van eigen stijlen, zodat de handoff één keer is vertaald.
 */

import styles from './ui.module.css';

export { styles as ui };

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'danger';

const BUTTON_CLASS: Record<ButtonVariant, string> = {
  primary: styles.buttonPrimary as string,
  secondary: styles.buttonSecondary as string,
  outline: styles.buttonOutline as string,
  danger: styles.buttonDanger as string,
};

export function Button({
  variant = 'outline',
  busy = false,
  children,
  className,
  disabled,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  busy?: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={[BUTTON_CLASS[variant], className].filter(Boolean).join(' ')}
      disabled={disabled || busy}
      {...rest}
    >
      {busy && <span className="spinner" aria-hidden="true" />}
      {children}
    </button>
  );
}

type ChipTone = 'neutral' | 'info' | 'muted' | 'warn' | 'danger' | 'success';

const CHIP_CLASS: Record<ChipTone, string> = {
  neutral: styles.chipNeutral as string,
  info: styles.chipInfo as string,
  muted: styles.chipMuted as string,
  warn: styles.chipWarn as string,
  danger: styles.chipDanger as string,
  success: styles.chipSuccess as string,
};

export function Chip({
  tone = 'neutral',
  children,
}: {
  tone?: ChipTone;
  children: React.ReactNode;
}): React.JSX.Element {
  return <span className={CHIP_CLASS[tone]}>{children}</span>;
}

type NoticeTone = 'error' | 'warn' | 'success' | 'info';

const NOTICE_CLASS: Record<NoticeTone, string> = {
  error: styles.noticeError as string,
  warn: styles.noticeWarn as string,
  success: styles.noticeSuccess as string,
  info: styles.noticeInfo as string,
};

const NOTICE_ICON: Record<NoticeTone, string> = {
  error: '⚠',
  warn: '⚠',
  success: '✓',
  info: 'ℹ',
};

/** Meldingen dragen naast kleur altijd een teken en tekst, zodat ze zonder kleur leesbaar zijn. */
export function Notice({
  tone = 'info',
  children,
}: {
  tone?: NoticeTone;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className={NOTICE_CLASS[tone]} role={tone === 'error' ? 'alert' : 'status'}>
      <span aria-hidden="true">{NOTICE_ICON[tone]}</span>
      <span>{children}</span>
    </div>
  );
}

export function Counter({
  value,
  label,
  tone,
}: {
  value: number | string;
  label: string;
  tone?: 'danger' | 'warn';
}): React.JSX.Element {
  const color =
    tone === 'danger' ? 'var(--c-up)' : tone === 'warn' ? 'var(--c-empty)' : 'var(--c-navy)';
  return (
    <div className={styles.counter}>
      <div className={styles.counterValue} style={{ color }}>
        {value}
      </div>
      <div className={styles.counterLabel}>{label}</div>
    </div>
  );
}

export function Card({
  title,
  description,
  children,
  actions,
}: {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className={styles.cardPadded}>
      {(title || actions) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 'var(--space-12)',
            marginBottom: description || children ? 'var(--space-12)' : 0,
          }}
        >
          <div>
            {title && <h2 className={styles.cardTitle}>{title}</h2>}
            {description && <p className={styles.cardSub}>{description}</p>}
          </div>
          {actions && <div style={{ display: 'flex', gap: 'var(--space-8)' }}>{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Link naar de productpagina bij een concurrent (US-021, US-023).
 *
 * Bij een prijs die opvalt is de eerste vraag "wat staat er dan op die pagina?". Het adres is
 * bekend — het is bij de bron vastgelegd — maar het moest via de bronconfigurator worden opgezocht.
 *
 * `rel="noopener noreferrer"` staat er om twee redenen: de andere pagina mag niet bij ons venster
 * kunnen, en de concurrent hoeft in zijn logboek niet te lezen vanaf welke pagina we komen.
 */
export function ExternLink({ url, naam }: { url: string; naam: string }): React.JSX.Element {
  return (
    <a
      className={styles.externLink}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title={`${naam} openen in een nieuw tabblad — ${url}`}
      aria-label={`Productpagina van ${naam} openen in een nieuw tabblad`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Het gebruikelijke teken voor "opent buiten deze applicatie". */}
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M14 4h6v6M20 4l-8.5 8.5M18 14v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </a>
  );
}
